import React, { useEffect, useState } from 'react';

import { animated, useSpring } from 'react-spring';
import moment from 'moment-timezone';

import AddressLookupField from 'views/AddressLookupField.js';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import DatePickerField from 'views/DatePickerField.js';
import { Editor, Element, Frame, useEditor, useNode } from '@craftjs/core';
import FilePickerField from 'views/FilePickerField.js';
import ImagePickerField from 'views/ImagePickerField.js';
import ListField from 'views/ListField.js';
import Sector from 'classes/Sector.js';
import SectorLookupField from 'views/SectorLookupField.js';
import TextField from 'views/TextField.js';
import Utils from 'files/Utils.js';

const ApplicationContext = React.createContext();

export const AnimatedContainer = ({ children, style, utils }) => {

    const [animations, setAnimations] = useSpring(() => ({
        top: -25,
        opacity: 0,
        config: { mass: 1, tension: 180, friction: 16 }
    }));

    const onRunAnimations = forward => {
        setAnimations({
            top: forward ? 0 : -25,
            opacity: forward ? 1 : 0
        });
    }

    useEffect(() => {
        onRunAnimations(true);
        utils.events.on('unmount', onRunAnimations.bind(this, false));
        return () => {
            utils.events.off('unmount', onRunAnimations.bind(this, false));
        }
    }, []);

    return (
        <animated.div style={{
            ...style,
            position: 'relative',
            ...animations
        }}>
            {children}
        </animated.div>
    )
}

const ButtonNode = ({ backgroundColor, color, fontSize, fontWeight, height, ignoreFieldRequirements, margin, openInNewWindow, padding, text, width }) => {

    const { id } = useNode();
    const { utils } = React.useContext(ApplicationContext) || {};

    const [animations, setAnimations] = useSpring(() => ({
        top: -25,
        opacity: 0,
        config: { mass: 1, tension: 180, friction: 16 }
    }));
    const [loading, setLoading] = useState(false);

    const onButtonClick = () => {
        utils.editor.button.onClick(id, { 
            ignore_field_requirements: ignoreFieldRequirements,
            open_in_new_window: openInNewWindow 
        });
    }

    const onLoadingChange = ({ detail }) => {
        setLoading(detail === id);
    }

    const onRunAnimations = forward => {
        setAnimations({
            top: forward ? 0 : -25,
            opacity: forward ? 1 : 0
        });
    }

    useEffect(() => {
        onRunAnimations(true);
        utils.events.on('loading', onLoadingChange);
        utils.events.on('unmount', onRunAnimations.bind(this, false));
        return () => {
            utils.events.off('loading', onLoadingChange);
            utils.events.off('unmount', onRunAnimations.bind(this, false));
        }
    }, []);

    return (
        <animated.div style={{
            position: 'relative',
            width: '100%',
            ...animations
        }}>
            <Button
            color={backgroundColor}
            loading={loading}
            onClick={onButtonClick}
            text={text}
            style={{
                color: color,
                fontSize: fontSize,
                fontWeight: fontWeight,
                height: height,
                width: width,
                ...formatEditorMargin(margin),
                ...formatEditorPadding(padding),
                ...animations
            }} />
        </animated.div>
    )
}

const Container = props => {

    const { children, margin, padding } = props;
    const { utils } = React.useContext(ApplicationContext) || {};

    const [animations, setAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        top: -25
    }));

    const onRunAnimations = forward => {
        setAnimations({
            opacity: forward ? 1 : 0,
            top: forward ? 0 : -25
        });
    }

    useEffect(() => {
        onRunAnimations(true);
        utils.events.on('unmount', onRunAnimations.bind(this, false));
        return () => {
            utils.events.off('unmount', onRunAnimations.bind(this, false));
        }
    }, []);

    return (
        <animated.div style={{
            position: 'relative',
            ...props,
            ...formatEditorMargin(margin),
            ...formatEditorPadding(padding),
            ...animations
        }}>
            {children}
        </animated.div>
    )
}

const ContentManager = ({ content }) => {

    const { actions } = useEditor();

    useEffect(() => {
        actions.deserialize(content);
    }, [content]);

    return (
        <Frame />
    );
}

const ContentViewer = ({ content, session, utils }) => {
    return (
        <ApplicationContext.Provider value={{
            session: session || {},
            utils: utils
        }}>
            <Editor
            enabled={false}
            resolver={{ ButtonNode, Container, Element, Field, Image, KeyValue, Text, Video }}>
                <ContentManager content={content} />
            </Editor>
        </ApplicationContext.Provider>
    )
}

const Field = ({ component, height, icon, identifier, list, margin, placeholder, required, secure, textfield_format, title, width }) => {

    const { utils } = React.useContext(ApplicationContext) || {};

    const [animations, setAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        top: -25
    }));

    const getComponent = () => {

        let value = utils.editor.field.get(identifier);
        switch(component.code) {
            case workflowLandingComponents.address_lookup:
            return (
                <AddressLookupField
                utils={utils}
                icon={icon}
                inline={false}
                insetLabel={title}
                placeholder={placeholder}
                prependComponent={getRequiredIndicator()}
                value={value && value.place}
                style={{
                    ...getStyles()
                }}
                onChange={result => {
                    utils.editor.field.set({
                        [identifier]: {
                            component: component.code,
                            value: result
                        }
                    });
                }}/>
            )

            case workflowLandingComponents.bool_list:
            return (
                <ListField
                deselect={false}
                icon={icon}
                insetLabel={title}
                prependComponent={getRequiredIndicator()}
                style={{
                    ...getStyles()
                }}
                value={typeof(value) === 'boolean' && {
                    id: value === true ? 'true' : 'false',
                    title: value === true ? 'Yes' : 'No'
                }}
                items={[{
                    id: 'true',
                    title: 'Yes'
                },{
                    id: 'false',
                    title: 'No'
                }]}
                onChange={item => {
                    utils.editor.field.set({
                        [identifier]: {
                            component: component.code,
                            value: item.id === 'true' ? true : false
                        }
                    });
                }}/>
            )

            case workflowLandingComponents.comm_link_serial_number_field:
            return (
                <TextField
                icon={icon}
                insetLabel={title}
                placeholder={placeholder}
                prependComponent={getRequiredIndicator()}
                isSecure={secure}
                value={value}
                containerStyle={{
                    ...getStyles()
                }}
                onChange={text => {
                    utils.editor.field.set({
                        [identifier]: {
                            component: component.code,
                            value:  text
                        }
                    });
                }}/>
            )

            case workflowLandingComponents.date_picker:
            return (
                <DatePickerField
                icon={icon}
                insetLabel={title}
                placeholder={placeholder}
                prependComponent={getRequiredIndicator()}
                utils={utils}
                value={value && moment.unix(value)}
                style={{
                    ...getStyles()
                }}
                onChange={date => {
                    utils.editor.field.set({
                        [identifier]: {
                            component: component.code,
                            value: date
                        }
                    });
                }}/>
            )

            case workflowLandingComponents.division_picker:
            case workflowLandingComponents.region_picker:
            return (
                <SectorLookupField
                icon={icon}
                insetLabel={title}
                placeholder={placeholder}
                prependComponent={getRequiredIndicator()}
                type={component.code === workflowLandingComponents.division_picker ? Sector.types.division : Sector.types.region}
                utils={utils}
                value={value}
                style={{
                    ...getStyles()
                }}
                onChange={result => {
                    utils.editor.field.set({
                        [identifier]: {
                            component: component.code,
                            value: result
                        }
                    });
                }}/>
            )

            case workflowLandingComponents.file_picker:
            return (
                <FilePickerField
                utils={utils}
                icon={icon}
                insetLabel={title}
                placeholder={placeholder}
                prependComponent={getRequiredIndicator()}
                value={value}
                style={{
                    ...getStyles()
                }}
                onChange={result => {
                    utils.editor.field.set({
                        [identifier]: {
                            component: component.code,
                            value: result
                        }
                    });
                }} />
            )

            case workflowLandingComponents.image_picker:
            return (
                <ImagePickerField
                utils={utils}
                icon={icon}
                insetLabel={title}
                placeholder={placeholder}
                prependComponent={getRequiredIndicator()}
                value={value}
                style={{
                    ...getStyles()
                }}
                onChange={result => {
                    utils.editor.field.set({
                        [identifier]: {
                            component: component.code,
                            value: result
                        }
                    });
                }}/>
            )

            case workflowLandingComponents.list:
            return (
                <ListField
                icon={icon}
                insetLabel={title}
                items={list}
                prependComponent={getRequiredIndicator()}
                value={value && list.find(item => item.id === value)}
                style={{
                    ...getStyles()
                }}
                onChange={item => {
                    utils.editor.field.set({
                        [identifier]: {
                            component: component.code,
                            value: item && item.title
                        }
                    });
                }}/>
            )

            case workflowLandingComponents.textfield:
            return (
                <TextField
                format={textfield_format}
                icon={icon}
                insetLabel={title}
                placeholder={placeholder}
                prependComponent={getRequiredIndicator()}
                isSecure={secure}
                value={value}
                containerStyle={{
                    ...getStyles()
                }}
                onChange={text => {
                    utils.editor.field.set({
                        [identifier]: {
                            component: component.code,
                            value:  text
                        }
                    });
                }}/>
            )

            case workflowLandingComponents.textview:
            return (
                <TextField
                expandWithText={true}
                format={textfield_format}
                icon={icon}
                placeholder={placeholder}
                prependComponent={getRequiredIndicator({
                    marginLeft: 12,
                    marginRight: 0,
                    marginTop: 16
                })}
                value={utils.editor.field.get(identifier)}
                containerStyle={{
                    ...getStyles(),
                    height: 'auto',
                    alignItems: 'flex-start'
                }}
                onChange={text => {
                    utils.editor.field.set({
                        [identifier]: {
                            component: component.code,
                            value:  text
                        }
                    });
                }}/>
            )

            case workflowLandingComponents.phone_number_field:
            return (
                <TextField
                format={'phone_number'}
                icon={icon}
                insetLabel={title}
                placeholder={placeholder}
                prependComponent={getRequiredIndicator()}
                isSecure={secure}
                value={value}
                containerStyle={{
                    ...getStyles()
                }}
                onChange={text => {
                    utils.editor.field.set({
                        [identifier]: {
                            component: component.code,
                            value:  text
                        }
                    });
                }}/>
            )

            case workflowLandingComponents.email_address_field:
            return (
                <TextField
                containerStyle={{
                    ...getStyles()
                }}
                format={'email_address'}
                icon={icon}
                insetLabel={title}
                onChange={text => {
                    utils.editor.field.set({
                        [identifier]: {
                            component: component.code,
                            value:  text
                        }
                    });
                }}
                placeholder={placeholder}
                prependComponent={getRequiredIndicator()}
                isSecure={secure}
                value={value} />
            )

            default:
            return null;
        }
    }

    const getRequiredIndicator = style => {
        let value = utils.editor.field.get(identifier);
        if(required === true && (value === null || value === undefined)) {
            return (
                <div style={{
                    backgroundColor: Appearance.colors.red,
                    borderRadius: 4,
                    height: 8,
                    marginRight: 8,
                    minHeight: 8,
                    minWidth: 8,
                    overflow: 'hidden',
                    width: 8,
                    ...style
                }} />
            )
        }
        return null;
    }

    const getStyles = () => ({
        height: height,
        width: width
    });

    const onRunAnimations = forward => {
        setAnimations({
            top: forward ? 0 : -25,
            opacity: forward ? 1 : 0
        });
    }

    useEffect(() => {
        onRunAnimations(true);
        utils.events.on('unmount', onRunAnimations.bind(this, false));
        return () => {
            utils.events.off('unmount', onRunAnimations.bind(this, false));
        }
    }, []);

    return (
        <animated.div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            position: 'relative',
            width: '100%',
            ...formatEditorMargin(margin),
            ...animations
        }}>
            {getComponent()}
        </animated.div>
    )
}

const Image = props => {

    const { imageAssetUrl, margin, padding } = props;
    const { utils } = React.useContext(ApplicationContext) || {};

    const [animations, setAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        top: -25
    }));

    const onRunAnimations = forward => {
        setAnimations({
            opacity: forward ? 1 : 0,
            top: forward ? 0 : -25
        });
    }

    const getBorderWidth = () => {
        return props.borderColor ? (props.borderWidth || 1) : 0;
    }

    useEffect(() => {
        onRunAnimations(true);
        utils.events.on('unmount', onRunAnimations.bind(this, false));
        return () => {
            utils.events.off('unmount', onRunAnimations.bind(this, false));
        }
    }, []);

    return (
        <animated.img
        src={imageAssetUrl}
        style={{
            ...props,
            alignSelf: Utils.isMobile() === true ? props.alignSelfMobile : props.alignSelfDesktop,
            borderStyle: getBorderWidth() > 0 ? 'solid' : null,
            position: 'relative',
            ...formatEditorMargin(margin),
            ...formatEditorPadding(padding),
            ...animations
        }}/>
    )
}

const KeyValue = ({ bottomBorder, padding, title, value }) => {

    const { utils } = React.useContext(ApplicationContext) || {};

    const [animations, setAnimations] = useSpring(() => ({
        top: -25,
        opacity: 0,
        config: { mass: 1, tension: 180, friction: 16 }
    }));

    const getFormattedValue = () => {
        if(value === null || value === undefined) {
            return 'Not available';
        }
        if(typeof(value) === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        return value;
    }

    const onRunAnimations = forward => {
        setAnimations({
            top: forward ? 0 : -25,
            opacity: forward ? 1 : 0
        });
    }

    useEffect(() => {
        onRunAnimations(true);
        utils.events.on('unmount', onRunAnimations.bind(this, false));
        return () => {
            utils.events.off('unmount', onRunAnimations.bind(this, false));
        }
    }, []);

    return (
        <animated.div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            borderBottom: bottomBorder ? `1px solid ${Appearance.colors.divider()}` : null,
            ...formatEditorPadding(padding),
            ...animations
        }}>
            <span style={{
                ...Appearance.textStyles.key(),
                textAlign: 'left'
            }}>{title}</span>
            <span style={{
                ...Appearance.textStyles.value(),
                textAlign: 'right'
            }}>{getFormattedValue()}</span>
        </animated.div>
    )
}

const Text = ({ color, darkModeColor, fontSize, fontWeight, margin, padding, textAlign, value, width }) => {

    const { utils } = React.useContext(ApplicationContext) || {};

    const [animations, setAnimations] = useSpring(() => ({
        top: -25,
        opacity: 0,
        config: { mass: 1, tension: 180, friction: 16 }
    }));

    const onRunAnimations = forward => {
        setAnimations({
            top: forward ? 0 : -25,
            opacity: forward ? 1 : 0
        });
    }

    const getValue = () => {
        return value ? value.replace('&nbsp;', '') : null;
    }

    useEffect(() => {
        onRunAnimations(true);
        utils.events.on('unmount', onRunAnimations.bind(this, false));
        return () => {
            utils.events.off('unmount', onRunAnimations.bind(this, false));
        }
    }, []);

    return (
        <animated.span style={{
            color: color,
            fontSize: fontSize,
            fontWeight: fontWeight,
            position: 'relative',
            textAlign: textAlign,
            width: width,
            ...formatEditorMargin(margin),
            ...formatEditorPadding(padding),
            ...animations,
            ...window.theme === 'dark' && {
                color: darkModeColor || '#FFFFFF'
            }
        }}>{getValue()}</animated.span>
    )
}

export const Video = props => {

    let { autoPlay, borderColor, borderRadius, borderWidth, controls, height, loop, margin, maxHeight, maxWidth, minHeight, minWidth, muted, objectFit, position, videoAssetUrl, width } = props;
    const { utils } = React.useContext(ApplicationContext) || {};

    const [animations, setAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        top: -25
    }));

    const onRunAnimations = forward => {
        setAnimations({
            opacity: forward ? 1 : 0,
            top: forward ? 0 : -25
        });
    }
    
    const getContent = () => {
        return (
            <animated.div style={{
                borderColor: borderColor || Appearance.colors.transparent,
                borderRadius: borderRadius || 8,
                borderWidth: borderWidth,
                height: height,
                maxHeight: maxHeight,
                minHeight: minHeight,
                maxWidth: maxWidth,
                minWidth: minWidth,
                overflow: 'hidden',
                position: position,
                width: width,
                ...formatEditorMargin(margin),
                ...animations,
                ...props
            }}>
                <video
                autoPlay={autoPlay}
                controls={controls}
                loop={loop}
                muted={muted}
                style={{
                    height: '100%',
                    objectFit: objectFit,
                    width: '100%'
                }}>
                    <source src={videoAssetUrl} />
                </video>
            </animated.div>
        )
    }

    useEffect(() => {
        onRunAnimations(true);
        utils.events.on('unmount', onRunAnimations.bind(this, false));
        return () => {
            utils.events.off('unmount', onRunAnimations.bind(this, false));
        }
    }, []);

    return getContent();
}

export const formatEditorMargin = margin => {
    if(!margin) {
        return null;
    }
    return {
        marginBottom: margin.bottom,
        marginLeft: margin.left,
        marginRight: margin.right,
        marginTop: margin.top
    }
}

export const formatEditorPadding = padding => {
    if(!padding) {
        return null;
    }
    return {
        paddingBottom: padding.bottom,
        paddingLeft: padding.left,
        paddingRight: padding.right,
        paddingTop: padding.top
    }
}

export const workflowLandingComponents = {
    address_lookup: 1,
    bool_list: 2,
    button: 3,
    checkbox: 4,
    color_picker: 5,
    container: 6,
    date_picker: 7,
    division_picker: 8,
    region_picker: 9,
    duration_picker: 10,
    file_picker: 11,
    image: 12,
    image_picker: 13,
    list: 14,
    multiple_list: 15,
    multiple_textfield: 16,
    phone_number_field: 17,
    text: 18,
    textfield: 19,
    textview: 20,
    time_picker: 21,
    email_address_field: 22,
    comm_link_serial_number_field: 23
}

export default ContentViewer;
